import { useMemo } from "react";
import { IoPeople } from "react-icons/io5";
import {
  Product,
  useNextAvailableGuidesQuery,
} from "../../../../../graphql/operations";
import { useSession } from "../../../../Atoms/AppSession";
import Loader from "../../../../Atoms/Loader";
import { CollapsibleProductContainer } from "../CollapsibleProductContainer";
import { CollapsibleProductContainerProps } from "../CollapsibleProductContainer/CollapsibleProductContainer";
import TopGuides from "../TopGuides";
import { AvailableGuides, AvailableGuidesProps } from "./AvailableGuides";

type CollapsibleAvailableGuidesProps = {
  product: AvailableGuidesProps["product"] & {
    location?: Pick<Product["location"], "timezone">;
  };
} & Pick<CollapsibleProductContainerProps, "abValue" | "isCollapsible">;
export function CollapsibleAvailableGuides({
  product,
  ...collapsibleProps
}: CollapsibleAvailableGuidesProps) {
  const session = useSession();
  const { loading, error, data } = useNextAvailableGuidesQuery({
    ssr: false,
    variables: {
      input: {
        productId: product.id,
        limit: 10, // next 10 available guides
      },
      lang: session.languageCode,
    },
  });

  const availableGuides = useMemo(
    () =>
      (data?.nextAvailableGuides ?? []).map((group) => ({
        timeSlot: { time: group.time },
        productAvailability: { date: group.date },
        guide: {
          id: group.mainGuide.id,
          nickName: group.mainGuide.nickName,
          profilePic: group.mainGuide.profilePic,
          introVideo: group.mainGuide.introVideo,
          numTours: group.mainGuide.numTours,
          numCustomers: group.mainGuide.numCustomers,
          translation: { description: group.mainGuide.translation.description },
        },
      })),
    [data]
  );

  if (loading) return <Loader />;
  if (error || !data) return <>error</>;

  if (availableGuides.length === 0) return <></>;

  return (
    <CollapsibleProductContainer
      label="Guides"
      Icon={IoPeople}
      ga4Label="guides"
      {...collapsibleProps}
    >
      {session.countryCode === product.location.timezone ? (
        <AvailableGuides product={product} availableGuides={availableGuides} />
      ) : (
        <TopGuides product={product} />
      )}
    </CollapsibleProductContainer>
  );
}
